// 使用 localStorage 替代 AsyncStorage 在 Web 环境中
const AsyncStorage = {
  async getItem(key: string): Promise<string | null> {
    try {
      return localStorage.getItem(key);
    } catch (error) {
      return null;
    }
  },
  async setItem(key: string, value: string): Promise<void> {
    try {
      localStorage.setItem(key, value);
    } catch (error) {
      // 忽略错误
    }
  },
  async removeItem(key: string): Promise<void> {
    try {
      localStorage.removeItem(key);
    } catch (error) {
      // 忽略错误
    }
  },
};

const SEARCH_HISTORY_KEY = 'nebula_art_search_history';
const MAX_HISTORY_ITEMS = 10;

export interface SearchHistoryItem {
  query: string;
  timestamp: number;
}

export class SearchHistoryManager {
  /**
   * 获取搜索历史
   */
  static async getSearchHistory(): Promise<string[]> {
    try {
      const historyJson = await AsyncStorage.getItem(SEARCH_HISTORY_KEY);
      if (!historyJson) return [];
      
      const history: SearchHistoryItem[] = JSON.parse(historyJson);
      
      // 按时间戳降序排序，返回查询字符串
      return history
        .sort((a, b) => b.timestamp - a.timestamp)
        .map(item => item.query);
    } catch (error) {
      console.error('Error getting search history:', error);
      return [];
    }
  }

  /**
   * 添加搜索记录
   */
  static async addSearchHistory(query: string): Promise<void> {
    if (!query.trim()) return;

    try {
      const existingHistory = await this.getSearchHistoryItems();
      
      // 移除已存在的相同查询
      const filteredHistory = existingHistory.filter(
        item => item.query.toLowerCase() !== query.toLowerCase()
      );

      // 添加新的查询到开头
      const newHistory: SearchHistoryItem[] = [
        { query: query.trim(), timestamp: Date.now() },
        ...filteredHistory
      ].slice(0, MAX_HISTORY_ITEMS); // 限制最大数量

      await AsyncStorage.setItem(SEARCH_HISTORY_KEY, JSON.stringify(newHistory));
    } catch (error) {
      console.error('Error adding search history:', error);
    }
  }

  /**
   * 移除特定搜索记录
   */
  static async removeSearchHistory(query: string): Promise<void> {
    try {
      const existingHistory = await this.getSearchHistoryItems();
      const filteredHistory = existingHistory.filter(
        item => item.query !== query
      );
      
      await AsyncStorage.setItem(SEARCH_HISTORY_KEY, JSON.stringify(filteredHistory));
    } catch (error) {
      console.error('Error removing search history:', error);
    }
  }

  /**
   * 清除所有搜索历史
   */
  static async clearSearchHistory(): Promise<void> {
    try {
      await AsyncStorage.removeItem(SEARCH_HISTORY_KEY);
    } catch (error) {
      console.error('Error clearing search history:', error);
    }
  }

  /**
   * 获取完整的搜索历史项目（包含时间戳）
   */
  private static async getSearchHistoryItems(): Promise<SearchHistoryItem[]> {
    try {
      const historyJson = await AsyncStorage.getItem(SEARCH_HISTORY_KEY);
      if (!historyJson) return [];
      
      return JSON.parse(historyJson);
    } catch (error) {
      console.error('Error getting search history items:', error);
      return [];
    }
  }

  /**
   * 获取搜索建议（基于历史记录）
   */
  static async getSearchSuggestions(query: string): Promise<string[]> {
    if (!query.trim()) return [];

    try {
      const history = await this.getSearchHistory();
      const lowerQuery = query.toLowerCase();
      
      // 筛选包含查询字符串的历史记录
      const suggestions = history.filter(item =>
        item.toLowerCase().includes(lowerQuery) && 
        item.toLowerCase() !== lowerQuery
      );

      return suggestions.slice(0, 5); // 最多返回5个建议
    } catch (error) {
      console.error('Error getting search suggestions:', error);
      return [];
    }
  }

  /**
   * 获取热门搜索词（基于搜索频率）
   */
  static async getPopularSearches(): Promise<string[]> {
    try {
      const historyItems = await this.getSearchHistoryItems();
      
      // 统计搜索频率
      const queryCount: { [key: string]: number } = {};
      historyItems.forEach(item => {
        const lowerQuery = item.query.toLowerCase();
        queryCount[lowerQuery] = (queryCount[lowerQuery] || 0) + 1;
      });

      // 按频率排序
      const sortedQueries = Object.entries(queryCount)
        .sort(([, a], [, b]) => b - a)
        .map(([query]) => query)
        .slice(0, 5);

      return sortedQueries;
    } catch (error) {
      console.error('Error getting popular searches:', error);
      return [];
    }
  }
}
